/**
 * Curva de avance (programado vs. real) en SVG. Dos series, un eje, leyenda + etiquetas directas y
 * tooltip por semana al pasar el mouse.
 */
import { useState } from 'react';

interface Props {
  programado: number[]; // % por semana (índice = semana)
  real: number[]; // % por semana hasta el corte
  semanaActual: number;
  semanaCorte: number;
  colorReal: string;
}

export function CurvaAvance({ programado, real, semanaActual, semanaCorte, colorReal }: Props) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 420;
  const H = 180;
  const m = { l: 34, r: 12, t: 12, b: 24 };
  const n = Math.max(programado.length - 1, 1);
  const x = (s: number) => m.l + (s / n) * (W - m.l - m.r);
  const y = (p: number) => m.t + (1 - p / 100) * (H - m.t - m.b);
  const linea = (vals: number[]) => vals.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  const colorProg = 'var(--text-secondary)';
  const ultimoReal = real.length - 1;
  return (
    <div>
      <div style={{ display: 'flex', gap: 14, fontSize: 'var(--fs-200)', color: 'var(--text-secondary)', marginBottom: 4 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="18" height="4"><line x1="0" y1="2" x2="18" y2="2" stroke={colorProg} strokeWidth="2" strokeDasharray="4 3" /></svg>
          Programado
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="18" height="4"><line x1="0" y1="2" x2="18" y2="2" stroke={colorReal} strokeWidth="2" /></svg>
          Real reportado
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const px = ((e.clientX - r.left) / r.width) * W;
          const s = Math.round(((px - m.l) / (W - m.l - m.r)) * n);
          setHover(s >= 0 && s <= n ? s : null);
        }}
      >
        {[0, 25, 50, 75, 100].map((p) => (
          <g key={p}>
            <line x1={m.l} x2={W - m.r} y1={y(p)} y2={y(p)} stroke="var(--border-subtle)" strokeWidth="1" />
            <text x={m.l - 6} y={y(p) + 3} fontSize="10" textAnchor="end" fill="var(--text-tertiary)">
              {p}%
            </text>
          </g>
        ))}
        {Array.from({ length: n + 1 }, (_, s) => s)
          .filter((s) => s % 2 === 0)
          .map((s) => (
            <text key={s} x={x(s)} y={H - 8} fontSize="10" textAnchor="middle" fill="var(--text-tertiary)">
              {s}
            </text>
          ))}
        <line x1={x(semanaCorte)} x2={x(semanaCorte)} y1={m.t} y2={H - m.b} stroke="var(--text-tertiary)" strokeDasharray="2 3" />
        <text x={x(semanaCorte) + 4} y={m.t + 9} fontSize="10" fill="var(--text-tertiary)">
          corte
        </text>
        <line x1={x(semanaActual)} x2={x(semanaActual)} y1={m.t} y2={H - m.b} stroke="var(--accent)" strokeWidth="1" opacity="0.6" />
        <path d={linea(programado)} fill="none" stroke={colorProg} strokeWidth="2" strokeDasharray="5 4" />
        <path d={linea(real)} fill="none" stroke={colorReal} strokeWidth="2" />
        {ultimoReal >= 0 && (
          <>
            <circle cx={x(ultimoReal)} cy={y(real[ultimoReal]!)} r="4" fill={colorReal} stroke="var(--bg-surface)" strokeWidth="2" />
            <text x={x(ultimoReal) - 6} y={y(real[ultimoReal]!) + 14} fontSize="10" textAnchor="end" fill="var(--text-primary)">
              {Math.round(real[ultimoReal]!)}% real
            </text>
            <text x={x(ultimoReal) + 6} y={y(programado[ultimoReal] ?? 0) - 6} fontSize="10" fill="var(--text-secondary)">
              {Math.round(programado[ultimoReal] ?? 0)}% prog.
            </text>
          </>
        )}
        {hover != null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={m.t} y2={H - m.b} stroke="var(--text-secondary)" strokeWidth="1" />
            <rect x={Math.min(x(hover) + 6, W - 130)} y={m.t + 14} width="122" height="42" rx="4" fill="var(--bg-raised)" stroke="var(--border-default)" />
            <text x={Math.min(x(hover) + 12, W - 124)} y={m.t + 28} fontSize="10" fill="var(--text-primary)">
              Semana {hover}
            </text>
            <text x={Math.min(x(hover) + 12, W - 124)} y={m.t + 40} fontSize="10" fill="var(--text-secondary)">
              Programado {Math.round(programado[hover] ?? 0)}%
            </text>
            <text x={Math.min(x(hover) + 12, W - 124)} y={m.t + 52} fontSize="10" fill="var(--text-secondary)">
              Real {hover < real.length ? `${Math.round(real[hover]!)}%` : '—'}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}
